import { type ComponentType, useCallback, useEffect, useMemo, useRef } from "react";
import { useRouter } from "solito/router";
import { match } from "ts-pattern";
import { FullscreenSpinner, isWeb } from "@medbillai/ui";
import { Flag, ONBOARDING_BILL_COMPLETION_DESTINATIONS, stripExperimentVariant } from "@medbillai/utils/flag";
import { loginUrl } from "@medbillai/utils/urls";
import { ErrorScreen } from "../../components/ErrorScreen";
import { analyticsService } from "../../lib/analytics";
import { onboardingVersionTag } from "../../lib/analytics/types";
import { useAuth } from "../../lib/auth";
import { evaluateEnumVariation } from "../../lib/flag";
import type { FlowState, FlowTimelineTransitionProps, FlowTransition } from "../../lib/flow-engine";
import { sentryService } from "../../lib/sentry";
import { classifyErrorSource } from "../../lib/sentry/classifyErrorSource";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import { usePushGetHelp } from "../../lib/usePushGetHelp";
import { deriveInitial } from "./deriveInitial";
import { trackScreenEnteredEvent, trackTransitionEvent } from "./flowTracking";
import { type AuthHandoff, ONBOARDING_VERSION, type OnboardingConfig, OnboardingFlowV3, Screen } from "./OnboardingFlowV3";
import type { ResolvedIntentData } from "./types";
import { useOnboardingState } from "./utils/useOnboardingState";
export type OnboardingPhase = "preauth" | "postauth";
const RESUME_ERROR_MESSAGE = "We couldn't load your onboarding progress. Please try again.";
export type OnboardingShellV3Props = {
  phase: OnboardingPhase;
  /** Per-platform screen-transition wrapper — see `OnboardingFlowV3`. */
  Transition: ComponentType<FlowTimelineTransitionProps>;
};

/**
 * The shared onboarding flow, for both entries. A bound partner is the only
 * thing that separates them, and it is read from the same resume query the
 * step derivation uses — so the caller never has to pick. Preauth renders the
 * value-prop beats and hands off to the standard login; postauth is the only
 * variant that reads the resume state, so it alone mounts that hook.
 */
export function OnboardingShellV3({
  phase,
  Transition
}: OnboardingShellV3Props) {
  return phase === "preauth" ? <PreauthShell Transition={Transition} data-sentry-element="PreauthShell" data-sentry-component="OnboardingShellV3" data-sentry-source-file="OnboardingShellV3.tsx" /> : <PostauthShell Transition={Transition} data-sentry-element="PostauthShell" data-sentry-component="OnboardingShellV3" data-sentry-source-file="OnboardingShellV3.tsx" />;
}
type ShellVariantProps = Pick<OnboardingShellV3Props, "Transition">;
function PreauthShell({
  Transition
}: ShellVariantProps) {
  const {
    replace
  } = useRouter();
  const setIsSignUpOnboarding = useSignUpOnboardingStore(s => s.setIsSignUpOnboarding);
  useEffect(() => {
    analyticsService.trackEvent("SignUpOnboardingFlowStarted", {
      onboardingVersion: ONBOARDING_VERSION
    });
    // Web has no preauth beats — the marketing site and the signup hero own
    // the value props, so web hands straight to the signup screen. The
    // signup framing rides the URL so it survives a refresh.
    if (isWeb) {
      setIsSignUpOnboarding(true);
      replace(loginUrl({
        variant: "signup"
      }));
    }
  }, [replace, setIsSignUpOnboarding]);
  const initial = useMemo(() => Screen.ValueProps(), []);

  // Web holds the spinner while the effect above redirects to signup.
  if (isWeb) {
    return <FullscreenSpinner viewportFloor />;
  }
  return <OnboardingFlowHost initial={initial} Transition={Transition} data-sentry-element="OnboardingFlowHost" data-sentry-component="PreauthShell" data-sentry-source-file="OnboardingShellV3.tsx" />;
}
function PostauthShell({
  Transition
}: ShellVariantProps) {
  const resume = useOnboardingState();
  const startedRef = useRef(false);
  useEffect(() => {
    if (resume.tag !== "loaded" || startedRef.current) {
      return;
    }
    startedRef.current = true;
    analyticsService.trackEvent("OnboardingResumeStarted", {
      onboardingVersion: onboardingVersionTag(ONBOARDING_VERSION, resume.state.partner?.slug)
    });
  }, [resume]);
  if (resume.tag === "loading") {
    return <FullscreenSpinner viewportFloor />;
  }
  // Fail closed: without the resume read there is no way to know where to
  // enter or whether a partner is bound, so hold entry behind a retry
  // instead of guessing.
  if (resume.tag === "unavailable") {
    return <ErrorScreen title="We experienced an error" message={RESUME_ERROR_MESSAGE} action={{
      label: "Try again",
      onPress: resume.retry
    }} report={{
      screen: "onboarding-resume",
      source: classifyErrorSource(resume.error, "apollo"),
      error: resume.error
    }} />;
  }
  return <OnboardingFlowHost initial={deriveInitial(resume.state)} partnerSlug={resume.state.partner?.slug} Transition={Transition} data-sentry-element="OnboardingFlowHost" data-sentry-component="PostauthShell" data-sentry-source-file="OnboardingShellV3.tsx" />;
}
function OnboardingFlowHost({
  initial,
  partnerSlug,
  Transition
}: ShellVariantProps & {
  initial: FlowState<OnboardingConfig>;
  partnerSlug?: string;
}) {
  const {
    push,
    replace
  } = useRouter();
  const setIsSignUpOnboarding = useSignUpOnboardingStore(s => s.setIsSignUpOnboarding);
  const {
    refreshProfile,
    getAndClearIntendedPath
  } = useAuth();
  const {
    pushGetHelp
  } = usePushGetHelp();
  const handleAuthHandoff = useCallback((event: AuthHandoff) => {
    match(event).with({
      tag: "exitToSignup"
    }, () => {
      setIsSignUpOnboarding(true);
      push(loginUrl({
        variant: "signup"
      }));
    }).with({
      tag: "exitToLogin"
    }, () => {
      push(loginUrl());
    }).exhaustive();
  }, [push, setIsSignUpOnboarding]);
  const handleComplete = useCallback(async ({
    intent
  }: ResolvedIntentData) => {
    setIsSignUpOnboarding(false);
    try {
      // Onboarding wrote segmentation (intent) post-auth; refresh the cached
      // profile so the home screen reflects it (e.g. bill-upload targeting)
      // instead of the stale login-time snapshot.
      await refreshProfile();
    } catch (error) {
      // Home refetches on its own — never strand the user on the spinner.
      sentryService.captureException(error);
    }
    // Resume the journey a gate (auth or onboarding) interrupted, else home.
    const intendedPath = getAndClearIntendedPath();
    if (intendedPath) {
      replace(intendedPath);
      return;
    }
    // Evaluating enrolls; a resume derived straight to Complete finished earlier.
    if (intent === "bill" && initial.tag !== "Complete") {
      const variant = evaluateEnumVariation(ONBOARDING_BILL_COMPLETION_DESTINATIONS, Flag.ONBOARDING_BILL_COMPLETION_DESTINATION, "home");
      analyticsService.trackEvent("OnboardingBillCompletionArmAssigned", {
        variant
      });
      if (stripExperimentVariant(variant) === "case") {
        pushGetHelp({
          caseType: "BILL_ISSUE_V2",
          title: "Get help with a bill",
          source: "onboarding_complete_bill",
          replaceRoute: true
        });
        return;
      }
    }
    replace("/");
  }, [replace, setIsSignUpOnboarding, refreshProfile, getAndClearIntendedPath, initial.tag, pushGetHelp]);
  const onboardingVersion = useMemo(() => onboardingVersionTag(ONBOARDING_VERSION, partnerSlug), [partnerSlug]);
  const onTransition = useCallback((event: FlowTransition<OnboardingConfig>) => trackTransitionEvent(event, onboardingVersion), [onboardingVersion]);

  // Covers the initial screen too. Auth-handoff outcomes are self-transitions
  // (fresh same-tag state committed while the shell redirects), so same-tag
  // commits are skipped to keep screen events 1:1 with genuine entries.
  const lastTrackedTagRef = useRef<FlowState<OnboardingConfig>["tag"] | null>(null);
  const trackScreenEntered = useCallback((state: FlowState<OnboardingConfig>) => {
    if (state.tag === lastTrackedTagRef.current) {
      return;
    }
    lastTrackedTagRef.current = state.tag;
    trackScreenEnteredEvent(state, onboardingVersion);
  }, [onboardingVersion]);
  return <OnboardingFlowV3 initial={initial} ctx={{
    isWeb
  }} onAuthHandoff={handleAuthHandoff} onComplete={handleComplete} onTransition={onTransition} onStateLoaded={trackScreenEntered} Transition={Transition} data-sentry-element="OnboardingFlowV3" data-sentry-component="OnboardingFlowHost" data-sentry-source-file="OnboardingShellV3.tsx" />;
}